import { useState, useEffect, useMemo } from "react";
import {
  subscribeVendors, addVendor, updateVendor,
  subscribeReferrals, addReferral, updateReferral, enableVendorPortal, addNotification,
} from "./firebase";
import { sendMail } from "./mailer";
import { commStatus, money, toINR, fetchRates } from "./commission";

const PORTAL_URL = "https://fm.neerajmandhana.com/vendors";

/* ═══════════════════════════════════════════════════════════════
   Trusted Partners — admin section (Phase 1)
   Vendor directory · recommend a student · referral tracking.
═══════════════════════════════════════════════════════════════ */

const INK = "#1c1a17", INK2 = "#4a463e", MUTE = "#8f8a7e";
const LINE = "#e7e3d8", PAPER = "#f3f1ea", GOLD = "#F5C518";

const STATUSES = ["Recommended", "In Progress", "Signed Up", "Did Not Sign Up", "Cancelled"];
const STATUS_FG = {
  "Recommended": "#1d4ed8", "In Progress": "#b45309", "Signed Up": "#15803d",
  "Did Not Sign Up": "#b91c1c", "Cancelled": "#6b7280",
};
const STATUS_BG = {
  "Recommended": "#e8eefc", "In Progress": "#fbeedb", "Signed Up": "#e3f3e8",
  "Did Not Sign Up": "#fbe6e6", "Cancelled": "#ecebe6",
};

const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const isEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

const STYLE = `
.tp-in{width:100%;padding:11px 13px;font-size:14px;font-family:inherit;color:#1c1a17;
  background:#fff;border:1px solid #ddd8c9;border-radius:10px;outline:none;box-sizing:border-box;
  transition:border-color .14s,box-shadow .14s;}
.tp-in::placeholder{color:#b6b0a0;}
.tp-in:focus{border-color:#F5C518;box-shadow:0 0 0 3px rgba(245,197,24,.2);}
.tp-sel{appearance:none;-webkit-appearance:none;cursor:pointer;padding-right:34px;
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8'><path d='M1 1.5 6 6.5 11 1.5' stroke='%238f8a7e' stroke-width='1.7' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat:no-repeat;background-position:right 12px center;}
.tp-btn{font-family:inherit;cursor:pointer;border:none;transition:transform .12s,box-shadow .14s,background .12s,border-color .12s;}
.tp-gold{background:#F5C518;color:#1c1a17;font-weight:800;border-radius:10px;box-shadow:0 1px 2px rgba(0,0,0,.14);}
.tp-gold:hover{transform:translateY(-1px);box-shadow:0 6px 18px rgba(245,197,24,.42);}
.tp-gold:active{transform:translateY(0);box-shadow:0 1px 2px rgba(0,0,0,.14);}
.tp-gold:disabled{background:#dcd8cc;color:#94907f;box-shadow:none;transform:none;cursor:default;}
.tp-ghost{background:transparent;color:#1c1a17;font-weight:700;border:1px solid #ddd8c9;border-radius:10px;}
.tp-ghost:hover{border-color:#1c1a17;}
.tp-tab{position:relative;background:none;border:none;cursor:pointer;font-family:inherit;
  padding:17px 2px;margin-right:30px;font-size:14px;font-weight:600;color:#9c978a;transition:color .14s;}
.tp-tab:hover{color:#4a463e;}
.tp-tab-on{color:#1c1a17;font-weight:700;}
.tp-tab-on:after{content:"";position:absolute;left:-2px;right:-2px;bottom:-1px;height:3px;background:#F5C518;border-radius:3px;}
.tp-seg{flex:1;padding:11px 8px;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer;
  background:#fff;border:1px solid #ddd8c9;color:#8f8a7e;transition:all .12s;}
.tp-seg:hover{color:#1c1a17;}
.tp-seg-on,.tp-seg-on:hover{background:#1c1a17;border-color:#1c1a17;color:#fff;}
.tp-row{transition:background .1s;}
.tp-row:hover{background:#faf8f1;}
.tp-sug{padding:10px 13px;cursor:pointer;font-size:13px;border-top:1px solid #f1efe6;transition:background .1s;}
.tp-sug:hover{background:#fffae6;}
.tp-link{background:none;border:none;cursor:pointer;font-family:inherit;color:#8a7415;font-weight:700;font-size:13px;padding:0;}
.tp-link:hover{text-decoration:underline;}
.tp-chip{font-family:inherit;cursor:pointer;border-radius:999px;font-size:12px;font-weight:700;
  padding:6px 13px;transition:all .12s;}
`;

const card = {
  background: "#fff", border: `1px solid ${LINE}`, borderRadius: 16,
  boxShadow: "0 1px 2px rgba(28,26,23,.04), 0 10px 30px -12px rgba(28,26,23,.12)",
};

/* ── tiny building blocks ── */
function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: MUTE, letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 6 }}>{children}</div>;
}
function Section({ index, title, children }) {
  return (
    <div style={{ padding: "26px 30px", borderTop: `1px solid ${LINE}` }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 18 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: GOLD, letterSpacing: ".1em" }}>{index}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: INK, letterSpacing: ".06em", textTransform: "uppercase" }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

const LOGO_URL = "https://fm.neerajmandhana.com/logo512.png";

// A plain, personal introduction email — looks like Neeraj wrote it himself.
function introEmailHtml({ studentName, vendorPoc, vendorOrg }) {
  const F = "font-family:Helvetica,Arial,sans-serif;";
  return `<!DOCTYPE html><html><head><meta charset="utf-8"></head>
<body style="margin:0;padding:30px 18px;background:#ffffff;">
  <div style="max-width:540px;margin:0 auto;${F}font-size:15px;line-height:1.75;color:#222222;">
    <img src="${LOGO_URL}" width="54" height="54" alt="NM Squad" style="display:block;margin-bottom:24px;">
    <p style="margin:0 0 15px;">Dear ${esc(vendorPoc || vendorOrg)},</p>
    <p style="margin:0 0 15px;">Having interacted with ${esc(studentName)}, I feel very confident that they will benefit from working closely with you.</p>
    <p style="margin:0 0 15px;">I request you to get in touch with the student and the family, explain your products and services, and help the family understand how it fits into their requirement.</p>
    <p style="margin:0 0 15px;">Thank you so much. I wish both sides all the best.</p>
    <p style="margin:24px 0 0;">Neeraj Mandhana</p>
  </div>
</body></html>`;
}

export default function TrustedPartners({ onBack, leads = [] }) {
  const [tab, setTab] = useState("recommend");
  const [vendors, setVendors] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [rates, setRates] = useState(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    const u1 = subscribeVendors(setVendors, () => setLoadErr(true));
    const u2 = subscribeReferrals(setReferrals, () => setLoadErr(true));
    return () => { u1 && u1(); u2 && u2(); };
  }, []);
  useEffect(() => { fetchRates().then(setRates); }, []);

  const active = referrals.filter(r => commStatus(r) !== "received");
  const completed = referrals.filter(r => commStatus(r) === "received");
  const TABS = [["recommend", "Recommend"], ["referrals", "Referrals"], ["completed", "Completed"], ["vendors", "Vendors"]];

  return (
    <div style={{ minHeight: "100vh", background: PAPER, fontFamily: "Helvetica, Arial, sans-serif" }}>
      <style>{STYLE}</style>

      {/* top bar */}
      <div style={{ background: INK, padding: "14px 26px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <span style={{ color: GOLD, fontSize: 11, fontWeight: 800, letterSpacing: ".18em" }}>NM SQUAD</span>
          <span style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>Trusted Partners</span>
        </div>
        <button className="tp-btn" onClick={onBack} style={{ fontSize: 13, color: "#cfcabb", background: "transparent", border: `1px solid #3a372f`, borderRadius: 9, padding: "8px 15px", fontWeight: 600 }}>
          Back to leads
        </button>
      </div>

      {/* tabs */}
      <div style={{ background: "#fff", borderBottom: `1px solid ${LINE}`, padding: "0 26px", display: "flex" }}>
        {TABS.map(([t, label]) => {
          const n = t === "referrals" ? active.length : t === "completed" ? completed.length : t === "vendors" ? vendors.length : null;
          return (
            <button key={t} className={"tp-tab" + (tab === t ? " tp-tab-on" : "")} onClick={() => setTab(t)}>
              {label}{n != null && <span style={{ marginLeft: 7, fontSize: 12, color: MUTE, fontWeight: 700 }}>{n}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ padding: "30px 26px 60px", maxWidth: 1080, margin: "0 auto" }}>
        {loadErr && (
          <div style={{ ...card, padding: "14px 18px", marginBottom: 20, borderColor: "#f0c9c9", background: "#fdf3f3", color: "#9a2222", fontSize: 13, fontWeight: 600 }}>
            Could not load partner data — the Firestore rules may not yet allow the vendors / referrals collections.
          </div>
        )}
        {tab === "recommend" && <RecommendTab vendors={vendors} leads={leads} onDone={() => setTab("referrals")} />}
        {tab === "referrals" && <ReferralsTab referrals={active} />}
        {tab === "completed" && <ReferralsTab referrals={completed} completed />}
        {tab === "vendors" && <VendorsTab vendors={vendors} referrals={referrals} rates={rates} />}
      </div>
    </div>
  );
}

/* ════════════════ RECOMMEND ════════════════ */
function RecommendTab({ vendors, leads, onDone }) {
  const [studentName, setStudentName] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [motherEmail, setMotherEmail] = useState("");
  const [fatherEmail, setFatherEmail] = useState("");
  const [leadId, setLeadId] = useState(null);
  const [picking, setPicking] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [product, setProduct] = useState("");
  const [meetingType, setMeetingType] = useState("TUAP");
  const [intro, setIntro] = useState("");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);

  const matches = useMemo(() => {
    const q = studentName.trim().toLowerCase();
    if (!q || !picking) return [];
    return leads.filter(l => [l.studentName, l.email, l.token].filter(Boolean).join(" ").toLowerCase().includes(q)).slice(0, 6);
  }, [studentName, picking, leads]);

  const vendor = vendors.find(v => v.id === vendorId);

  const pickLead = (l) => {
    setStudentName(l.studentName || ""); setStudentEmail(l.email || "");
    setMotherEmail(l.parentEmail || ""); setFatherEmail("");
    setLeadId(l.firebaseId || null); setPicking(false);
  };
  const reset = () => {
    setStudentName(""); setStudentEmail(""); setMotherEmail(""); setFatherEmail("");
    setLeadId(null); setVendorId(""); setProduct(""); setIntro(""); setMeetingType("TUAP");
  };

  const submit = async () => {
    setMsg(null);
    if (!studentName.trim()) return setMsg({ ok: false, t: "Enter the student name." });
    if (!isEmail(studentEmail)) return setMsg({ ok: false, t: "A valid student email is required." });
    if (!vendor) return setMsg({ ok: false, t: "Choose a partner." });
    if (!isEmail(vendor.pocEmail)) return setMsg({ ok: false, t: "This partner has no contact email — add one in the Vendors tab first." });
    if (motherEmail && !isEmail(motherEmail)) return setMsg({ ok: false, t: "The mother email looks invalid." });
    if (fatherEmail && !isEmail(fatherEmail)) return setMsg({ ok: false, t: "The father email looks invalid." });

    setSending(true);
    // Addressed to the partner; the student and family are on Cc.
    const cc = [studentEmail, motherEmail, fatherEmail].map(e => (e || "").trim()).filter(isEmail).join(", ");
    const mail = await sendMail({
      to: vendor.pocEmail.trim(), cc, fromName: "Neeraj Mandhana",
      subject: "Introduction: " + studentName.trim(),
      html: introEmailHtml({ studentName: studentName.trim(), vendorOrg: vendor.organization, vendorPoc: vendor.pocName }),
    });
    const now = new Date().toISOString();
    try {
      await addReferral({
        studentName: studentName.trim(), studentEmail: studentEmail.trim(),
        motherEmail: motherEmail.trim(), fatherEmail: fatherEmail.trim(),
        studentLeadId: leadId || null,
        vendorId: vendor.id, vendorName: vendor.organization, vendorPocEmail: vendor.pocEmail || "",
        product: product.trim(), meetingType, intro: intro.trim(), recommendedBy: "Neeraj Mandhana",
        status: "Recommended", statusHistory: [{ status: "Recommended", at: now }],
        commissionPct: vendor.commissionPct || "", commissionAmount: vendor.commissionAmount || "",
        emailSentAt: mail.ok ? now : null, emailOk: !!mail.ok, notes: "",
      });
    } catch (e) {
      setSending(false);
      return setMsg({ ok: false, t: "Intro email " + (mail.ok ? "sent" : "failed") + ", but saving the referral failed: " + e.message });
    }
    setSending(false);
    if (mail.ok) {
      setMsg({ ok: true, t: "Recommendation sent and logged. Intro email delivered to the student, parents and " + vendor.organization + "." });
      reset(); setTimeout(onDone, 1500);
    } else {
      setMsg({ ok: false, t: "Referral saved, but the intro email failed (" + (mail.error || "unknown") + "). Check the partner's email address." });
    }
  };

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: GOLD, letterSpacing: ".16em", marginBottom: 6 }}>NEW RECOMMENDATION</div>
        <h1 style={{ margin: 0, fontSize: 27, fontWeight: 800, color: INK, letterSpacing: "-.01em" }}>Recommend a student to a partner</h1>
        <p style={{ margin: "8px 0 0", fontSize: 14, color: INK2, lineHeight: 1.6 }}>
          One introduction email goes out from nm@neerajmandhana.com — the student in To, the parents and the partner contact in Cc.
        </p>
      </div>

      <div style={card}>
        {/* 01 — student */}
        <Section index="01" title="Student">
          <div style={{ position: "relative", marginBottom: 14 }}>
            <Label>Student name</Label>
            <input className="tp-in" value={studentName} placeholder="Search leads, or type a TUAP student name"
              onChange={e => { setStudentName(e.target.value); setPicking(true); setLeadId(null); }} />
            {matches.length > 0 && (
              <div style={{ position: "absolute", zIndex: 6, left: 0, right: 0, background: "#fff", border: `1px solid ${LINE}`, borderRadius: 10, marginTop: 5, overflow: "hidden", boxShadow: "0 12px 28px -8px rgba(28,26,23,.22)" }}>
                {matches.map(l => (
                  <div key={l.firebaseId} className="tp-sug" onClick={() => pickLead(l)}>
                    <b style={{ color: INK }}>{l.studentName}</b>
                    <span style={{ color: MUTE }}> · {l.email || "no email"} · {l.token}</span>
                  </div>
                ))}
              </div>
            )}
            {leadId && <div style={{ fontSize: 11, color: "#15803d", fontWeight: 700, marginTop: 6 }}>Linked to an existing lead</div>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><Label>Student email</Label>
              <input className="tp-in" value={studentEmail} onChange={e => setStudentEmail(e.target.value)} placeholder="student@email.com" /></div>
            <div><Label>Mother email</Label>
              <input className="tp-in" value={motherEmail} onChange={e => setMotherEmail(e.target.value)} placeholder="optional" /></div>
            <div><Label>Father email</Label>
              <input className="tp-in" value={fatherEmail} onChange={e => setFatherEmail(e.target.value)} placeholder="optional" /></div>
          </div>
        </Section>

        {/* 02 — partner */}
        <Section index="02" title="Partner & program">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div><Label>Partner</Label>
              <select className="tp-in tp-sel" value={vendorId} onChange={e => setVendorId(e.target.value)}>
                <option value="">Select a partner</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.organization}</option>)}
              </select></div>
            <div><Label>Product / program</Label>
              <input className="tp-in" value={product} onChange={e => setProduct(e.target.value)} placeholder="e.g. SAT Prep, Research Program" /></div>
          </div>
          {vendor && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", fontSize: 12.5, color: INK2, background: "#faf8f1", border: `1px solid ${LINE}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
              <span>Contact: <b style={{ color: INK }}>{vendor.pocName || "—"}</b></span>
              <span style={{ color: vendor.pocEmail ? INK2 : "#b91c1c" }}>{vendor.pocEmail || "no email on file"}</span>
              {vendor.commissionPct && <span>Commission: <b style={{ color: INK }}>{vendor.commissionPct}</b></span>}
            </div>
          )}
          <Label>Meeting type</Label>
          <div style={{ display: "flex", borderRadius: 10, overflow: "hidden", maxWidth: 320 }}>
            {[["FM", "First Meeting"], ["TUAP", "TUAP"]].map(([v, l], i) => (
              <button key={v} type="button" className={"tp-btn tp-seg" + (meetingType === v ? " tp-seg-on" : "")}
                onClick={() => setMeetingType(v)}
                style={{ borderRadius: i === 0 ? "10px 0 0 10px" : "0 10px 10px 0", borderRight: i === 0 ? "none" : undefined }}>{l}</button>
            ))}
          </div>
        </Section>

        {/* 03 — intro */}
        <Section index="03" title="Introduction">
          <Label>A short note on the student (shared with the partner)</Label>
          <textarea className="tp-in" value={intro} onChange={e => setIntro(e.target.value)}
            style={{ minHeight: 96, resize: "vertical", lineHeight: 1.6 }}
            placeholder="Grade, interests, and what the student is looking for." />
        </Section>

        {/* footer */}
        <div style={{ padding: "22px 30px", borderTop: `1px solid ${LINE}`, background: "#faf8f1", borderRadius: "0 0 16px 16px" }}>
          {msg && (
            <div style={{ marginBottom: 14, padding: "11px 15px", borderRadius: 10, fontSize: 13, fontWeight: 600, lineHeight: 1.5,
              background: msg.ok ? "#e3f3e8" : "#fbe6e6", color: msg.ok ? "#156534" : "#9a2222" }}>{msg.t}</div>
          )}
          <button className="tp-btn tp-gold" onClick={submit} disabled={sending}
            style={{ padding: "13px 28px", fontSize: 14.5 }}>
            {sending ? "Sending…" : "Send recommendation"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════ REFERRALS ════════════════ */
function pingEmailHtml({ vendorOrg, studentName }) {
  const F = "font-family:Helvetica,Arial,sans-serif;";
  return `<!DOCTYPE html><html><body style="margin:0;padding:24px;background:#ebe9e1;">
  <div style="max-width:520px;margin:0 auto;background:#fff;border-radius:14px;overflow:hidden;border:1px solid #e7e3d8;">
    <div style="background:#161412;padding:18px 26px;">
      <div style="${F}font-size:16px;color:#fff;font-weight:700;">NM Squad</div>
      <div style="${F}font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#F5C518;font-weight:700;margin-top:3px;">Partner Portal</div>
    </div>
    <div style="height:3px;background:#F5C518;"></div>
    <div style="padding:24px 26px;${F}font-size:14.5px;line-height:1.7;color:#3c392f;">
      <p style="margin:0 0 14px;">Dear ${esc(vendorOrg)},</p>
      <p style="margin:0 0 14px;">A reminder that the commission for <b>${esc(studentName)}</b> is now due to NM Squad.</p>
      <p style="margin:0 0 14px;">Please transfer it and record the details in your partner portal:
        <a href="${PORTAL_URL}" style="color:#8a7415;font-weight:700;">${PORTAL_URL}</a></p>
      <p style="margin:16px 0 0;">Warm regards,<br><b style="color:#161412;">NM Squad</b></p>
    </div>
  </div></body></html>`;
}

function ReferralsTab({ referrals, completed }) {
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");
  const changeStatus = (r, status) =>
    updateReferral(r.id, { status, statusHistory: [...(r.statusHistory || []), { status, at: new Date().toISOString() }] });
  const shown = filter ? referrals.filter(r => (r.status || "") === filter) : referrals;
  const pendingCount = referrals.filter(r => commStatus(r) === "pending").length;

  const doPing = async (r) => {
    await addNotification({
      vendorId: r.vendorId, referralId: r.id, title: "Commission requested",
      body: "NM Squad has requested the commission for " + r.studentName + ". Please transfer it and record the details in your portal.",
    });
    await updateReferral(r.id, { commissionPinged: { at: new Date().toISOString() } });
    if (r.vendorPocEmail)
      await sendMail({ to: r.vendorPocEmail, subject: "NM Squad — commission request",
        html: pingEmailHtml({ vendorOrg: r.vendorName, studentName: r.studentName }) });
  };
  const ping = async (r) => { setBusy(r.id); try { await doPing(r); } catch (e) { alert("Ping failed: " + e.message); } setBusy(""); };
  const pingAll = async () => {
    const pend = referrals.filter(r => commStatus(r) === "pending");
    if (!pend.length || !window.confirm("Ping all " + pend.length + " vendors with a pending commission?")) return;
    setBusy("all");
    for (const r of pend) { try { await doPing(r); } catch (e) { /* continue */ } }
    setBusy("");
  };
  const confirmReceived = (r) => {
    if (!window.confirm("Confirm that NM Squad has received this commission?")) return;
    updateReferral(r.id, { commissionStatus: "received", commissionReceived: { at: new Date().toISOString() } });
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 14, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 800, color: INK }}>{completed ? "Completed referrals" : "Referrals"}</h1>
          <p style={{ margin: "0 0 18px", fontSize: 14, color: INK2 }}>
            {completed ? "Referrals where the commission has been received and the loop is closed." : "Every student recommended to a partner, with live status and commission."}</p>
        </div>
        {pendingCount > 0 && (
          <button className="tp-btn tp-gold" onClick={pingAll} disabled={busy === "all"}
            style={{ padding: "9px 16px", fontSize: 13, marginBottom: 18 }}>
            {busy === "all" ? "Pinging…" : "Ping all pending (" + pendingCount + ")"}
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        <button className="tp-btn tp-chip" onClick={() => setFilter("")}
          style={chipStyle(filter === "", INK, "#fff")}>All ({referrals.length})</button>
        {STATUSES.map(s => {
          const n = referrals.filter(r => r.status === s).length;
          return <button key={s} className="tp-btn tp-chip" onClick={() => setFilter(s)}
            style={chipStyle(filter === s, STATUS_FG[s], STATUS_BG[s])}>{s} ({n})</button>;
        })}
      </div>

      {referrals.length === 0 ? (
        <EmptyState
          title={completed ? "No completed referrals yet" : "No referrals yet"}
          body={completed ? "Referrals appear here once their commission is received." : "Use the Recommend tab to create the first one."} />
      ) : (
        <div style={{ ...card, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ background: "#faf8f1" }}>
              {["Student", "Partner", "Type", "Status", "Commission", "Date"].map(h =>
                <th key={h} style={thStyle}>{h}</th>)}
            </tr></thead>
            <tbody>
              {shown.map(r => (
                <tr key={r.id} className="tp-row" style={{ borderTop: `1px solid ${LINE}` }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, color: INK }}>{r.studentName}</div>
                    <div style={{ fontSize: 11.5, color: MUTE }}>{r.product || "—"}</div>
                    {r.emailOk === false && <div style={{ fontSize: 11, color: "#b91c1c", fontWeight: 700, marginTop: 2 }}>intro email failed</div>}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: INK2 }}>{r.vendorName}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: INK2, background: PAPER, border: `1px solid ${LINE}`, borderRadius: 6, padding: "3px 8px" }}>{r.meetingType}</span>
                  </td>
                  <td style={tdStyle}>
                    <select className="tp-sel" value={r.status || "Recommended"} onChange={e => changeStatus(r, e.target.value)}
                      style={{ appearance: "none", WebkitAppearance: "none", fontFamily: "inherit", fontSize: 12, fontWeight: 700, padding: "6px 30px 6px 11px",
                        borderRadius: 999, border: "none", cursor: "pointer",
                        color: STATUS_FG[r.status] || INK, background: STATUS_BG[r.status] || PAPER }}>
                      {STATUSES.map(s => <option key={s} value={s} style={{ color: INK, background: "#fff" }}>{s}</option>)}
                    </select>
                  </td>
                  <td style={{ ...tdStyle, minWidth: 190 }}>
                    <CommissionCell r={r} busy={busy === r.id} onPing={() => ping(r)} onConfirm={() => confirmReceived(r)} />
                  </td>
                  <td style={{ ...tdStyle, color: MUTE, fontSize: 12 }}>{fmtDate(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CommissionCell({ r, busy, onPing, onConfirm }) {
  const cs = commStatus(r);
  if (cs === "none") return <span style={{ color: MUTE }}>—</span>;

  if (cs === "received")
    return <span style={{ fontSize: 12, fontWeight: 800, color: "#15803d" }}>Received</span>;

  if (cs === "paid") {
    const p = r.commPayment || {};
    return (
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: "#8a5a06" }}>
          Paid{p.amount ? " · " + p.amount + " " + (p.currency || "") : ""}
        </div>
        {p.combined && <div style={{ fontSize: 10.5, color: MUTE }}>part of a combined payment</div>}
        {p.reference && <div style={{ fontSize: 10.5, color: MUTE }}>Ref: {p.reference}</div>}
        <button className="tp-btn tp-gold" onClick={onConfirm} style={{ marginTop: 5, padding: "5px 11px", fontSize: 11.5 }}>Confirm received</button>
      </div>
    );
  }

  // pending
  return (
    <div>
      <button className="tp-btn tp-ghost" onClick={onPing} disabled={busy}
        style={{ padding: "5px 11px", fontSize: 11.5 }}>
        {busy ? "Pinging…" : r.commissionPinged ? "Ping again" : "Ping for commission"}
      </button>
      {r.commissionPinged && <div style={{ fontSize: 10.5, color: "#8a5a06", fontWeight: 700, marginTop: 3 }}>Pinged {fmtDate(r.commissionPinged.at)}</div>}
    </div>
  );
}

/* ════════════════ VENDORS ════════════════ */
function VendorsTab({ vendors, referrals, rates }) {
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const byVendor = useMemo(() => {
    const m = {};
    vendors.forEach(v => { m[v.id] = { pending: 0, received: 0 }; });
    (referrals || []).forEach(r => {
      const x = m[r.vendorId]; if (!x) return;
      const cs = commStatus(r);
      const inr = toINR(r.commAmount, r.commCurrency, rates) || 0;
      if (cs === "received") x.received += inr;
      else if (cs === "pending" || cs === "paid") x.pending += inr;
    });
    return m;
  }, [vendors, referrals, rates]);
  const totalPending = Object.values(byVendor).reduce((s, x) => s + x.pending, 0);
  const totalReceived = Object.values(byVendor).reduce((s, x) => s + x.received, 0);

  const shown = q ? vendors.filter(v => (v.organization + " " + v.category + " " + v.pocName).toLowerCase().includes(q.toLowerCase())) : vendors;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 800, color: INK }}>Partner directory</h1>
          <p style={{ margin: 0, fontSize: 14, color: INK2 }}>{vendors.length} trusted partners.</p>
        </div>
        <button className="tp-btn tp-gold" onClick={() => setEditing({})} style={{ padding: "10px 18px", fontSize: 13.5 }}>Add partner</button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{ ...card, padding: "14px 20px", flex: "1 1 200px" }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: MUTE, textTransform: "uppercase", letterSpacing: ".06em" }}>Pending from all partners</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#b91c1c", marginTop: 3 }}>Rs {money(totalPending)}</div>
        </div>
        <div style={{ ...card, padding: "14px 20px", flex: "1 1 200px" }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: MUTE, textTransform: "uppercase", letterSpacing: ".06em" }}>Received from all partners</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#15803d", marginTop: 3 }}>Rs {money(totalReceived)}</div>
        </div>
      </div>

      <input className="tp-in" value={q} onChange={e => setQ(e.target.value)} placeholder="Search partners by name, category or contact"
        style={{ marginBottom: 16, maxWidth: 420 }} />

      {vendors.length === 0 ? (
        <EmptyState title="No partners loaded" body="The vendors collection is empty or not yet readable." />
      ) : (
        <div style={{ ...card, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ background: "#faf8f1" }}>
                {["Organization", "Category", "Contact", "Pending", "Received", ""].map(h =>
                  <th key={h} style={thStyle}>{h}</th>)}
              </tr></thead>
              <tbody>
                {shown.map(v => {
                  const cm = byVendor[v.id] || { pending: 0, received: 0 };
                  return (
                    <tr key={v.id} className="tp-row" style={{ borderTop: `1px solid ${LINE}` }}>
                      <td style={{ ...tdStyle, fontWeight: 700, color: INK }}>{v.organization}</td>
                      <td style={{ ...tdStyle, color: MUTE }}>{v.category || "—"}</td>
                      <td style={tdStyle}>
                        <div style={{ color: INK2 }}>{v.pocName || "—"}</div>
                        <div style={{ fontSize: 11.5, color: v.pocEmail ? MUTE : "#b91c1c" }}>{v.pocEmail || "no email"}</div>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: cm.pending ? "#b91c1c" : MUTE }}>{cm.pending ? "Rs " + money(cm.pending) : "—"}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: cm.received ? "#15803d" : MUTE }}>{cm.received ? "Rs " + money(cm.received) : "—"}</td>
                      <td style={tdStyle}><button className="tp-link" onClick={() => setEditing(v)}>Open</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {editing && <VendorModal vendor={editing} referrals={referrals} rates={rates} onClose={() => setEditing(null)} />}
    </div>
  );
}

function inviteEmailHtml({ vendorOrg, pocName, email }) {
  const F = "font-family:Helvetica,Arial,sans-serif;";
  const setupUrl = PORTAL_URL + "?setup=" + encodeURIComponent(email);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"></head>
<body style="margin:0;padding:0;background:#ebe9e1;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#ebe9e1;"><tr>
<td align="center" style="padding:36px 16px;">
  <table role="presentation" width="560" cellpadding="0" cellspacing="0" style="max-width:560px;width:100%;background:#fff;border-radius:16px;overflow:hidden;">
    <tr><td style="background:#161412;padding:22px 30px;">
      <table role="presentation" cellpadding="0" cellspacing="0"><tr>
        <td style="padding-right:13px;"><img src="${LOGO_URL}" width="44" height="44" alt="NM Squad" style="display:block;"></td>
        <td><div style="${F}font-size:17px;color:#fff;font-weight:700;">NM Squad</div>
            <div style="${F}font-size:10.5px;letter-spacing:.14em;text-transform:uppercase;color:#F5C518;font-weight:700;margin-top:3px;">Partner Portal</div></td>
      </tr></table>
    </td></tr>
    <tr><td style="height:4px;background:#F5C518;font-size:0;">&nbsp;</td></tr>
    <tr><td style="padding:28px 30px;${F}font-size:14.5px;line-height:1.7;color:#3c392f;">
      <p style="margin:0 0 14px;">Dear ${esc(pocName || vendorOrg)},</p>
      <p style="margin:0 0 20px;">NM Squad has set up portal access for <b>${esc(vendorOrg)}</b>. Click below to set your password — you will then see the students we introduce to you and can keep their status updated.</p>
      <table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 0 20px;"><tr>
        <td style="border-radius:10px;background:#F5C518;">
          <a href="${setupUrl}" style="display:inline-block;padding:13px 30px;${F}font-size:15px;font-weight:800;color:#161412;text-decoration:none;">Set your password</a>
        </td>
      </tr></table>
      <p style="margin:0 0 4px;${F}font-size:12.5px;color:#9b937a;">If the button does not work, paste this link into your browser:</p>
      <p style="margin:0 0 16px;${F}font-size:12.5px;word-break:break-all;"><a href="${setupUrl}" style="color:#8a7415;">${setupUrl}</a></p>
      <p style="margin:18px 0 0;">Warm regards,</p>
      <p style="margin:4px 0 0;font-weight:700;color:#161412;">NM Squad</p>
    </td></tr>
    <tr><td style="border-top:1px solid #efece0;padding:16px 30px;${F}font-size:12px;color:#9b937a;">
      <a href="mailto:nm@neerajmandhana.com" style="color:#8a7415;font-weight:700;text-decoration:none;">nm@neerajmandhana.com</a>
    </td></tr>
  </table>
</td></tr></table></body></html>`;
}

function VendorModal({ vendor, referrals, rates, onClose }) {
  const isNew = !vendor.id;
  const myRefs = (referrals || []).filter(r => r.vendorId === vendor.id);
  const pending = myRefs.filter(r => commStatus(r) === "pending");
  const paid = myRefs.filter(r => commStatus(r) === "paid");
  const received = myRefs.filter(r => commStatus(r) === "received");
  const sumINR = (list) => list.reduce((s, r) => s + (toINR(r.commAmount, r.commCurrency, rates) || 0), 0);
  const [f, setF] = useState({
    organization: vendor.organization || "", category: vendor.category || "", price: vendor.price || "",
    commissionPct: vendor.commissionPct || "", commissionAmount: vendor.commissionAmount || "",
    pocName: vendor.pocName || "", pocEmail: vendor.pocEmail || "", mouUrl: vendor.mouUrl || "",
  });
  const [saving, setSaving] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [portalMsg, setPortalMsg] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.organization.trim()) return;
    setSaving(true);
    try {
      if (isNew) await addVendor(f); else await updateVendor(vendor.id, f);
      onClose();
    } catch (e) { setSaving(false); alert("Save failed: " + e.message); }
  };

  const enablePortal = async () => {
    setPortalMsg(null);
    if (!vendor.pocEmail) return setPortalMsg({ ok: false, t: "Add a contact email and Save first." });
    setEnabling(true);
    try {
      await enableVendorPortal(vendor);
      const mail = await sendMail({
        to: vendor.pocEmail,
        subject: "Your NM Squad partner portal access",
        html: inviteEmailHtml({ vendorOrg: vendor.organization, pocName: vendor.pocName, email: vendor.pocEmail }),
      });
      setPortalMsg({ ok: true, t: mail.ok
        ? "Portal access enabled — invite emailed to " + vendor.pocEmail
        : "Portal access enabled. The invite email did not send — tell the vendor to open the portal and create a login with " + vendor.pocEmail + "." });
    } catch (e) {
      setPortalMsg({ ok: false, t: "Could not enable: " + e.message });
    }
    setEnabling(false);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,26,23,.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, padding: 0, maxWidth: 460, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${LINE}` }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: INK }}>{isNew ? "Add partner" : vendor.organization}</h3>
        </div>
        <div style={{ padding: 24 }}>
          {!isNew && myRefs.length > 0 && (
            <div style={{ background: "#faf8f1", border: `1px solid ${LINE}`, borderRadius: 10, padding: "14px 16px", marginBottom: 18 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: MUTE, letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 8 }}>Commission summary</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 22px", fontSize: 13, color: INK2 }}>
                <span>Students referred: <b style={{ color: INK }}>{myRefs.length}</b></span>
                <span>Pending: <b style={{ color: "#b91c1c" }}>{pending.length} · Rs {money(sumINR(pending))}</b></span>
                <span>Paid (awaiting): <b style={{ color: "#8a5a06" }}>{paid.length} · Rs {money(sumINR(paid))}</b></span>
                <span>Received: <b style={{ color: "#15803d" }}>{received.length} · Rs {money(sumINR(received))}</b></span>
              </div>
            </div>
          )}
          {[["organization", "Organization"], ["category", "Category"], ["price", "Price"],
            ["commissionPct", "Commission %"], ["commissionAmount", "Commission amount"],
            ["pocName", "Contact name"], ["pocEmail", "Contact email"], ["mouUrl", "MoU link"]].map(([k, label]) => (
            <div key={k} style={{ marginBottom: 12 }}>
              <Label>{label}</Label>
              <input className="tp-in" value={f[k]} onChange={set(k)} />
            </div>
          ))}

          {!isNew && (
            <div style={{ borderTop: `1px solid ${LINE}`, marginTop: 8, paddingTop: 16 }}>
              <Label>Partner portal access</Label>
              {vendor.portalEnabled ? (
                <div style={{ fontSize: 13, color: "#15803d", fontWeight: 700 }}>Enabled for {vendor.pocEmail}</div>
              ) : (
                <>
                  <p style={{ fontSize: 12.5, color: MUTE, margin: "2px 0 10px", lineHeight: 1.5 }}>
                    Creates a login for {vendor.pocEmail || "this contact"} and emails an invite with a temporary password.
                  </p>
                  <button className="tp-btn tp-ghost" onClick={enablePortal} disabled={enabling}
                    style={{ padding: "9px 16px", fontSize: 13 }}>{enabling ? "Enabling…" : "Enable portal access & send invite"}</button>
                </>
              )}
              {portalMsg && <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600, color: portalMsg.ok ? "#15803d" : "#b91c1c" }}>{portalMsg.t}</div>}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button className="tp-btn tp-gold" onClick={save} disabled={saving} style={{ padding: "11px 22px", fontSize: 14 }}>{saving ? "Saving…" : "Save"}</button>
            <button className="tp-btn tp-ghost" onClick={onClose} style={{ padding: "11px 22px", fontSize: 14 }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── shared bits ── */
function EmptyState({ title, body }) {
  return (
    <div style={{ ...card, padding: "52px 30px", textAlign: "center" }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>{title}</div>
      <div style={{ fontSize: 13.5, color: MUTE, marginTop: 6 }}>{body}</div>
    </div>
  );
}
function chipStyle(active, fg) {
  return active
    ? { background: fg, color: "#fff", border: `1px solid ${fg}` }
    : { background: "#fff", color: MUTE, border: `1px solid ${LINE}` };
}
const thStyle = { padding: "11px 14px", fontSize: 10.5, color: MUTE, textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 800, textAlign: "left" };
const tdStyle = { padding: "12px 14px", verticalAlign: "top" };
function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}
